import { BibleSiteGettable } from "./typedefs/BibleSiteStorage";
import {
  OfflineState,
  Site,
  Bible,
  Book,
  Chapter
} from "./typedefs/BibleOffline";
import { Status, statusFromHttp, success } from "./typedefs/RequestStatus";

export type fetchFunction = (
  input: RequestInfo,
  init?: RequestInit
) => Promise<Response>;
const SUPPORTED_DOMAINS = [
  "global.bible",
  "lvh.me",
  "global-bible-prod.us-east-1.elasticbeanstalk.com",
  "gbibl.com"
];

export function isSupportedDomain(fullDomain: string): boolean {
  return SUPPORTED_DOMAINS.some(supportedDomain =>
    fullDomain.endsWith(supportedDomain)
  );
}

function unslash(path: string) {
  return path.replace(/\/+$/, "");
}

// API gets Bible and Site data from api.bible and api.global.bible.
export class Api implements BibleSiteGettable {
  bibleApiHeaders: { [header: string]: string } = {};

  constructor(
    private fetch: fetchFunction, // in a browser, use window.fetch.bind(window)
    private bibleApiKey: string,
    private bibleApi = "https://api.scripture.api.bible/v1",
    private siteApi = "https://api.global.bible",
    private online = true
  ) {
    this.bibleApi = unslash(this.bibleApi);
    this.siteApi = unslash(this.siteApi);
    this.bibleApiHeaders["Content-Type"] = "application/json";
    this.bibleApiHeaders["api-key"] = this.bibleApiKey;
    // EXCLUDE FROM PRODUCTION
    if (
      !fetch ||
      (window &&
        window.fetch &&
        (window.fetch === fetch || fetch.name.indexOf("bound") < 0))
    )
      console.warn(
        "You probably want to pass in window.fetch.bind(window), otherwise web requests will fail."
      );
  }

  name() {
    return "ApiSiteSource";
  }

  setOnline(isOnline: boolean) {
    this.online = isOnline;
  }

  // GET request to api.global.bible
  async siteGET(path: string): Promise<[any, number]> {
    try {
      const url = this.siteApi + path;
      const rsp = await this.fetch(url);
      const data = await rsp.json();
      return [data, rsp.status];
    } catch (e) {
      return [JSON.stringify(e), 500];
    }
  }

  // GET request to api.bible
  async bibleGET(
    pathWithQuery: string,
    cache: RequestCache = "default"
  ): Promise<[any, number]> {
    try {
      const rsp = await this.fetch(`${this.bibleApi}${pathWithQuery}`, {
        headers: this.bibleApiHeaders,
        cache
      });
      const data = await rsp.json();
      return [data, rsp.status];
    } catch (e) {
      return [JSON.stringify(e), 500];
    }
  }

  // Get privacy policy
  // LATER: this needs refactoring to work offline in a PWA
  getPolicyStatement(publicUrl: string): Promise<any | undefined> {
    return this.fetch(`${publicUrl}/privacyPolicyStatement.json`).then(
      response => response.json().then(data => data.policy)
    );
  }

  // Get Bible site metadata, including Bibles (shallow, without books and chapters)Site
  async getSite(
    fullSiteDomain: string
  ): Promise<{ site: Site | undefined; status: Status }> {
    let rsp: any, httpStatus: number;
    if (isSupportedDomain(fullSiteDomain)) {
      const subdomain = fullSiteDomain.split(".")[0];
      [rsp, httpStatus] = await this.siteGET(
        `/sites/findOne?subdomain=${subdomain}`
      );
    } else {
      [rsp, httpStatus] = await this.siteGET(
        `/sites/findOne?domain=${fullSiteDomain}`
      );
    }
    const status = statusFromHttp(httpStatus);
    const site = success(status) ? (rsp.site as Site) : undefined;
    if (site) {
      site.domain = fullSiteDomain; // MAYBE only set this if site.domain is empty?
      site.offlineState = OfflineState.remote;
      if (site.bibles)
        site.bibles.forEach(b => (b.offlineState = OfflineState.remote));
    }
    return { site, status };
  }

  // Get Bible info string and related audio bibles
  async getBible(
    bibleId: string
  ): Promise<{ bible: Bible | undefined; status: Status }> {
    const [body, httpStatus] = await this.bibleGET(`/bibles/${bibleId}`);
    const status = statusFromHttp(httpStatus);
    const bible = success(status) ? (body.data as Bible) : undefined;
    return { bible, status };
  }

  // Get details book and chapter info about the Bible (but no text in the chapters)
  async getBooks(
    bibleId: string
  ): Promise<{ books: Book[] | undefined; status: Status }> {
    const [body, httpStatus] = await this.bibleGET(
      `/bibles/${bibleId}/books?include-chapters=true`
    );
    const status = statusFromHttp(httpStatus);
    const books = success(status) ? (body.data as Book[]) : undefined;
    return { books, status };
  }

  // Get the text of a chapter in the JSON format parsed into a JavaScript object
  async getChapter(
    bibleId: string,
    chapterId: string,
    parallelBibleIds: string[] = []
  ): Promise<{ chapter: Chapter | undefined; status: Status }> {
    let chapter: Chapter | undefined = undefined;
    let queryString =
      "content-type=json&include-verse-spans=true&include-notes=true";
    if (parallelBibleIds.length)
      queryString += `&parallels=${parallelBibleIds.join(",")}`;
    const url = `/bibles/${bibleId}/chapters/${chapterId}?${queryString}`;
    const [body, httpStatus] = await this.bibleGET(url);
    const status = statusFromHttp(httpStatus);
    if (success(status)) {
      body.data.fumsId = body.meta.fumsId;
      chapter = body.data as Chapter;
      chapter.fumsId = body.meta && body.meta.fumsId;
    }
    return { chapter, status };
  }
}
